/**
 * Control Registry Builder
 * Registers all built-in controls with the renderer
 */

import type { IControlComponentRegistry } from './ControlRenderer';
import { getGlobalControlRegistry } from './ControlRenderer';
import { getGlobalReactRegistry } from './ReactControlRegistry';
import { seoEffect } from './formEffects/seoEffect';
import { analyticsEffect } from './formEffects/analyticsEffect';
import { securityEffect } from './formEffects/securityEffect';
import { complianceEffect } from './formEffects/complianceEffect';
import { brandingEffect } from './formEffects/brandingEffect';
import { behaviorEffect } from './formEffects/behaviorEffect';
import { notificationEffect } from './formEffects/notificationEffect';
import { embedEffect } from './formEffects/embedEffect';

// Phase 1 - Input Controls (14)
import TextInput from './inputs/TextInput';
import TextareaInput from './inputs/TextareaInput';
import NumberInput from './inputs/NumberInput';
import EmailInput from './inputs/EmailInput';
import PasswordInput from './inputs/PasswordInput';
import UrlInput from './inputs/UrlInput';
import SelectInput from './inputs/SelectInput';
import MultiSelectInput from './inputs/MultiSelectInput';
import CheckboxInput from './inputs/CheckboxInput';
import RadioInput from './inputs/RadioInput';
import SwitchInput from './inputs/SwitchInput';
import DateInput from './inputs/DateInput';
import DatetimeInput from './inputs/DatetimeInput';
import JsonEditorInput from './inputs/JsonEditorInput';
import ButtonControl from './inputs/ButtonControl';
import JsFileSelectorControl from './inputs/JsFileSelectorControl';
import CssFileSelectorControl from './inputs/CssFileSelectorControl';

// Phase 1 - Display Controls (5 basic)
import LabelControl from './displays/LabelControl';
import HeaderControl from './displays/HeaderControl';
import ImageControl from './displays/ImageControl';
import HtmlControl from './displays/HtmlControl';
import FileUploadControl from './displays/FileUploadControl';
import LinkControl from './displays/LinkControl';

// Additional Display Controls
import { VideoControl } from './displays/VideoControl';
import { AudioControl } from './displays/AudioControl';
import { IframeViewerControl } from './displays/IframeViewerControl';
import { PdfViewerControl } from './displays/PdfViewerControl';
import { ArticleControl } from './displays/ArticleControl';
import { MermaidControl } from './displays/MermaidControl';
import { TreeViewControl } from './displays/TreeViewControl';
import OrgChartControl from './displays/OrgChartControl';

// Phase 3 - Advanced Input Controls (12)
import { ColorPickerComponent } from './inputs/ColorPickerComponent';
import { SliderRangeComponent } from './inputs/SliderRangeComponent';
import { DateRangePickerComponent } from './inputs/DateRangePickerComponent';
import { TimePickerComponent } from './inputs/TimePickerComponent';
import { TreeSelectComponent } from './inputs/TreeSelectComponent';
import { CascadingSelectComponent } from './inputs/CascadingSelectComponent';
import { MultiSelectSearchComponent } from './inputs/MultiSelectSearchComponent';
import { TagInputComponent } from './inputs/TagInputComponent';
import { LocationPickerComponent } from './inputs/LocationPickerComponent';
import { EnhancedJsonEditorComponent } from './inputs/EnhancedJsonEditorComponent';
import { KeyValuePairsComponent } from './inputs/KeyValuePairsComponent';
import { FormPickerComponent } from './inputs/FormPickerComponent';

// Layout Controls
import { TabsControl } from './layouts/TabsControl';
import { AccordionControl } from './layouts/AccordionControl';
import { CardContainerControl } from './layouts/CardContainerControl';
import { StepperControl } from './layouts/StepperControl';
import { CollapsiblePanelControl } from './layouts/CollapsiblePanelControl';
import { ModalControl } from './layouts/ModalControl';
import { BreadcrumbControl } from './layouts/BreadcrumbControl';
import { SidebarNavControl } from './layouts/SidebarNavControl';
import { DataTableControl } from './layouts/DataTableControl';
import { ApiResponseViewerControl } from './layouts/ApiResponseViewerControl';
import { VariableInspectorControl } from './layouts/VariableInspectorControl';
import { FormContainerControl } from './layouts/FormContainerControl';
import { GridControl } from './inputs/GridControl';

// Store the registry for late registration of advanced controls
let globalRegistry: IControlComponentRegistry | null = null;

/**
 * Register all Phase 1 controls (input + basic display)
 */
export const registerPhase1Controls = (
  registry: IControlComponentRegistry = getGlobalControlRegistry(),
): void => {
  // Input Controls
  registry.register('text', TextInput);
  registry.register('textarea', TextareaInput);
  registry.register('number', NumberInput);
  registry.register('email', EmailInput);
  registry.register('password', PasswordInput);
  registry.register('url', UrlInput);
  registry.register('select', SelectInput);
  registry.register('multiselect', MultiSelectInput);
  registry.register('checkbox', CheckboxInput);
  registry.register('radio', RadioInput);
  registry.register('switch', SwitchInput);
  registry.register('date', DateInput);
  registry.register('datetime', DatetimeInput);
  registry.register('json-editor', JsonEditorInput);

  // Button Control (for form submission and actions)
  registry.register('button', ButtonControl as any);
  registry.register('link', LinkControl);

  // Display Controls
  registry.register('label', LabelControl);
  registry.register('header', HeaderControl);
  registry.register('image', ImageControl);
  registry.register('html', HtmlControl);
  registry.register('file-upload', FileUploadControl);
  registry.register('js-file-selector', JsFileSelectorControl as any);
  registry.register('css-file-selector', CssFileSelectorControl as any);

  // Additional Display Controls
  registry.register('video', VideoControl as any);
  registry.register('audio', AudioControl as any);
  registry.register('iframe-viewer', IframeViewerControl as any);
  registry.register('pdf-viewer', PdfViewerControl as any);
  registry.register('article', ArticleControl as any);
  registry.register('mermaid', MermaidControl as any);
  registry.register('tree-view', TreeViewControl as any);
  registry.register('org-chart', OrgChartControl as any);

  // Layout Controls
  registry.register('enhanced-tabs', TabsControl as any);
  registry.register('accordion', AccordionControl as any);
  registry.register('card-container', CardContainerControl as any);
  registry.register('stepper', StepperControl as any);
  registry.register('collapsible-panel', CollapsiblePanelControl as any);
  registry.register('modal', ModalControl as any);
  registry.register('breadcrumb', BreadcrumbControl as any);
  registry.register('sidebar-nav', SidebarNavControl as any);
  registry.register('data-table', DataTableControl as any);
  registry.register('api-response-viewer', ApiResponseViewerControl as any);
  registry.register('variable-inspector', VariableInspectorControl as any);
  registry.register('form-container', FormContainerControl as any);
  registry.register('form-node', FormContainerControl as any);
  registry.register('grid', GridControl as any);
};

/**
 * Get count of registered Phase 1 controls
 */
export const getPhase1ControlCount = (): number => {
  return 25; // 14 input + 11 display
};

/**
 * Get list of Phase 1 control types
 */
export const getPhase1ControlTypes = (): string[] => {
  return [
    // Input Controls
    'text',
    'textarea',
    'number',
    'email',
    'password',
    'url',
    'select',
    'multiselect',
    'checkbox',
    'radio',
    'switch',
    'date',
    'datetime',
    'json-editor',
    // Display Controls
    'label',
    'header',
    'image',
    'html',
    'file-upload',
    // Additional Display Controls
    'video',
    'audio',
    'iframe-viewer',
    'pdf-viewer',
    'article',
    'mermaid',
  ];
};

/**
 * Check if a control type is in Phase 1
 */
export const isPhase1Control = (type: string): boolean => {
  return getPhase1ControlTypes().includes(type);
};

/**
 * Register all Phase 3 controls (advanced input)
 */
export const registerPhase3Controls = (
  registry: IControlComponentRegistry = getGlobalControlRegistry(),
): void => {
  registry.register('color-picker', ColorPickerComponent as any);
  registry.register('slider-range', SliderRangeComponent as any);
  registry.register('date-range-picker', DateRangePickerComponent as any);
  registry.register('time-picker', TimePickerComponent as any);
  registry.register('tree-select', TreeSelectComponent as any);
  registry.register('cascading-select', CascadingSelectComponent as any);
  registry.register('multi-select-search', MultiSelectSearchComponent as any);
  registry.register('tag-input', TagInputComponent as any);
  registry.register('location-picker', LocationPickerComponent as any);
  registry.register('enhanced-json-editor', EnhancedJsonEditorComponent as any);
  registry.register('key-value-pairs', KeyValuePairsComponent as any);
  registry.register('form-picker', FormPickerComponent as any);
};

/**
 * Get count of registered Phase 3 controls
 */
export const getPhase3ControlCount = (): number => {
  return 12;
};

/**
 * Get list of Phase 3 control types
 */
export const getPhase3ControlTypes = (): string[] => {
  return [
    'color-picker',
    'slider-range',
    'date-range-picker',
    'time-picker',
    'tree-select',
    'cascading-select',
    'multi-select-search',
    'tag-input',
    'location-picker',
    'enhanced-json-editor',
    'key-value-pairs',
    'form-picker',
  ];
};

/**
 * Check if a control type is in Phase 3
 */
export const isPhase3Control = (type: string): boolean => {
  return getPhase3ControlTypes().includes(type);
};

/**
 * Register all controls including advanced controls
 * Combines Phase 1, Phase 3, and advanced controls from controls-advanced-react
 *
 * @example
 * import { registerAllControls } from '@atlas-forms/player-components-react';
 * registerAllControls(); // Call once at app startup
 */
/**
 * Register all 9 form-scope plugin controls in the React registry.
 * These are non-visual (no EditComponent) — they run effects at the form level.
 * Call once at app startup alongside registerAllControls().
 */
export const registerFormScopePlugins = (): void => {
  const reactRegistry = getGlobalReactRegistry();

  const plugins = [
    {
      type: 'seo-settings' as const,
      label: 'SEO Settings',
      group: 'Other',
      description: 'Meta tags, Open Graph, robots directives, structured data',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: seoEffect,
      defaultConfig: {
        metaTitle: '', metaDescription: '', ogTitle: '', ogDescription: '',
        ogImage: '', robotsDirective: 'index', canonicalUrl: '', slugUrl: '', faviconUrl: '',
      },
    },
    {
      type: 'analytics-settings' as const,
      label: 'Analytics',
      group: 'Other',
      description: 'Google Analytics, Tag Manager, Facebook Pixel, UTM tracking',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: analyticsEffect,
      defaultConfig: {
        googleAnalyticsId: '', googleTagManagerId: '', facebookPixelId: '',
        linkedInInsightTagId: '', conversionEventName: '', utmCaptureEnabled: false,
        trackPartialSubmissions: false,
      },
    },
    {
      type: 'security-settings' as const,
      label: 'Security',
      group: 'Other',
      description: 'CAPTCHA, honeypot, rate limiting, password protection',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: securityEffect,
      defaultConfig: {
        captchaType: 'none', honeypotEnabled: false, passwordProtected: false,
        formPassword: '', submissionsPerIpLimit: 0, rateLimitWindowMinutes: 60,
        closedAfterDate: '', closedAfterCount: 0,
      },
    },
    {
      type: 'compliance-settings' as const,
      label: 'Compliance',
      group: 'Other',
      description: 'GDPR consent, HIPAA mode, data retention, audit trail',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: complianceEffect,
      defaultConfig: {
        gdprConsentEnabled: false, gdprConsentText: '', dataRetentionDays: 0,
        hipaaMode: false, auditTrailEnabled: false, privacyPolicyUrl: '', termsUrl: '',
      },
    },
    {
      type: 'branding-settings' as const,
      label: 'Branding',
      group: 'Other',
      description: 'Logo, colors, fonts, header/footer HTML, powered-by badge',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: brandingEffect,
      defaultConfig: {
        logoUrl: '', logoPosition: 'left', primaryColor: '', accentColor: '',
        backgroundColor: '', fontFamily: '', headerHtml: '', footerHtml: '',
        hidePoweredByBadge: false,
      },
    },
    {
      type: 'behavior-settings' as const,
      label: 'Behavior',
      group: 'Other',
      description: 'Confirmation, redirect, auto-save, progress bar, multi-submissions',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: behaviorEffect,
      defaultConfig: {
        confirmationType: 'message', confirmationMessage: 'Thank you!', redirectUrl: '',
        redirectDelaySeconds: 0, allowMultipleSubmissions: true, autoSaveEnabled: false,
        showProgressBar: false,
      },
    },
    {
      type: 'notification-settings' as const,
      label: 'Notifications',
      group: 'Other',
      description: 'Email notifications, auto-responder messages',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: notificationEffect,
      defaultConfig: {
        notificationSubject: '', autoResponderEnabled: false,
        autoResponderEmailField: '', autoResponderSubject: '',
      },
    },
    {
      type: 'embed-settings' as const,
      label: 'Embed & Share',
      group: 'Other',
      description: 'Embed type, popup trigger, iframe resize, share link, QR code',
      pluginScope: 'form-only' as const,
      singleton: true,
      formEffect: embedEffect,
      defaultConfig: {
        embedType: 'inline', popupTrigger: 'load', popupDelaySeconds: 0,
        iframeAutoResize: true, shareLinkEnabled: false, qrCodeEnabled: false,
      },
    },
    {
      type: 'custom-code' as const,
      label: 'Custom Code',
      group: 'Other',
      description: 'Inject HTML/CSS into head, body start, body end',
      pluginScope: 'form-only' as const,
      singleton: true,
      defaultConfig: {
        headHtml: '', bodyStartHtml: '', bodyEndHtml: '', inlineStyles: '',
      },
    },
  ];

  let registered = 0;
  for (const plugin of plugins) {
    // Idempotent: re-running registerAllControls() (e.g. React 18 StrictMode's
    // double-invoked mount effect) must not throw on a control already present.
    if (reactRegistry.has(plugin.type)) {
      registered++;
      continue;
    }
    try {
      reactRegistry.registerReact(plugin);
      registered++;
    } catch (err: unknown) {
      console.error(`[registerFormScopePlugins] Failed to register '${plugin.type}':`, err instanceof Error ? err.message : String(err));
    }
  }
  console.log(`[registerFormScopePlugins] Registered ${registered}/${plugins.length} form-scope plugins`);
};

let allControlsRegistered = false;

export const registerAllControls = (
  registry: IControlComponentRegistry = getGlobalControlRegistry(),
): void => {
  if (allControlsRegistered) {
    return;
  }
  allControlsRegistered = true;
  globalRegistry = registry;

  registerPhase1Controls(registry);
  registerPhase3Controls(registry);
  try {
    registerFormScopePlugins();
  } catch (err: unknown) {
    console.error('[registerAllControls] Form scope plugin registration failed:', err instanceof Error ? err.message : String(err));
  }

  import('@atlas-forms/controls-advanced-react')
    .then((module) => {
      module.registerAdvancedControls(registry);
    })
    .catch((err: unknown) => {
      console.warn('[registerAllControls] Advanced controls unavailable:', err instanceof Error ? err.message : String(err));
    });
};
